import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button } from "@material-tailwind/react";
import { CalendarDaysIcon, PencilSquareIcon, PlusCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import EmptyState from "../components/EmptyState";
import FormField from "../components/FormField";
import FormSelect from "../components/FormSelect";
import LoadingScreen from "../components/LoadingScreen";
import SectionCard from "../components/SectionCard";
import StatCard from "../components/StatCard";
import { useAuth } from "../context/AuthContext";
import { deleteAppointment, saveAppointment, subscribeByOwner } from "../services/db";
import { formatDateTime, toEnglishDigits } from "../utils/format";

const initialForm = {
  title: "",
  date: new Date().toISOString().slice(0, 10),
  time: "",
  projectName: "",
  status: "pending",
  notes: ""
};

const STATUS_OPTIONS = [
  { value: "pending", label: "قيد المتابعة" },
  { value: "done", label: "مكتمل" },
  { value: "canceled", label: "ملغي" }
];

export default function AppointmentsPage() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editId, setEditId] = useState(null);
  const [alert, setAlert] = useState(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const unsubscribe = subscribeByOwner("appointments", user.uid, setAppointments);
    const timer = setTimeout(() => setReady(true), 250);
    return () => {
      unsubscribe?.();
      clearTimeout(timer);
    };
  }, [user?.uid]);

  const sortedAppointments = useMemo(() => [...appointments].sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`)), [appointments]);
  const pendingCount = useMemo(() => appointments.filter((item) => item.status === "pending").length, [appointments]);
  const doneCount = useMemo(() => appointments.filter((item) => item.status === "done").length, [appointments]);

  const resetForm = () => {
    setForm(initialForm);
    setEditId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);
    try {
      setBusy(true);
      await saveAppointment({ ...form, ownerUid: user.uid }, editId);
      setAlert({ color: "green", message: editId ? "تم تعديل الموعد بنجاح." : "تمت إضافة الموعد بنجاح." });
      resetForm();
    } catch (err) {
      setAlert({ color: "red", message: err?.message || "تعذر حفظ الموعد." });
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setForm({
      title: item.title || "",
      date: item.date || new Date().toISOString().slice(0, 10),
      time: item.time || "",
      projectName: item.projectName || "",
      status: item.status || "pending",
      notes: item.notes || ""
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("هل تريد حذف هذا الموعد؟")) return;
    try {
      await deleteAppointment(id);
      setAlert({ color: "green", message: "تم حذف الموعد." });
    } catch {
      setAlert({ color: "red", message: "تعذر حذف الموعد." });
    }
  };

  if (!ready) return <LoadingScreen text="جارٍ تحميل دفتر المواعيد..." />;

  return (
    <div className="page-shell space-y-4">
      <section className="mini-grid">
        <StatCard title="إجمالي المواعيد" value={toEnglishDigits(appointments.length)} tone="brand" />
        <StatCard title="قيد المتابعة" value={toEnglishDigits(pendingCount)} tone="accent" />
        <StatCard title="المكتملة" value={toEnglishDigits(doneCount)} tone="success" />
        <StatCard title="المجدولة" value={toEnglishDigits(sortedAppointments.length)} icon={CalendarDaysIcon} tone="danger" />
      </section>

      <SectionCard title={editId ? "تعديل موعد" : "إضافة موعد جديد"} subtitle="نموذج مرتب للمواعيد مع ربط اختياري بمشروع أو عميل">
        {alert ? <Alert color={alert.color} className="mb-4 rounded-2xl text-sm">{alert.message}</Alert> : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="form-grid">
            <FormField label="عنوان الموعد" placeholder="مثال: اجتماع مع عميل" value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} required />
            <FormField type="date" dir="ltr" label="التاريخ" value={form.date} onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))} required />
            <FormField type="time" dir="ltr" label="الوقت" value={form.time} onChange={(e) => setForm((prev) => ({ ...prev, time: e.target.value }))} />
            <FormField label="المشروع أو العميل" placeholder="اختياري" value={form.projectName} onChange={(e) => setForm((prev) => ({ ...prev, projectName: e.target.value }))} />
            <FormSelect label="الحالة" value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))} options={STATUS_OPTIONS} />
            <div className="sm:col-span-2 xl:col-span-3">
              <FormField as="textarea" label="تفاصيل الموعد" placeholder="اكتب الملاحظات أو المطلوب متابعته..." value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} />
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button type="submit" className="rounded-2xl bg-brand-700" disabled={busy}>
              <span className="flex items-center gap-2"><PlusCircleIcon className="h-5 w-5" />{editId ? "حفظ التعديل" : "إضافة الموعد"}</span>
            </Button>
            {editId ? <Button type="button" variant="outlined" className="rounded-2xl border-brand-200 text-brand-700" onClick={resetForm}>إلغاء</Button> : null}
          </div>
        </form>
      </SectionCard>

      <SectionCard title="قائمة المواعيد" subtitle="كل المواعيد المرتبطة بأعمالك ومتابعاتك">
        {!sortedAppointments.length ? (
          <EmptyState title="لا توجد مواعيد" description="ابدأ بإضافة موعد جديد للمتابعة والتنظيم." />
        ) : (
          <div className="space-y-3">
            {sortedAppointments.map((item) => (
              <div key={item.id} className="mobile-card">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-extrabold text-slate-800 sm:text-lg">{item.title}</h3>
                      <span className={`rounded-full px-3 py-1 text-[11px] font-extrabold ${item.status === "done" ? "bg-emerald-50 text-income" : item.status === "canceled" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"}`}>
                        {item.status === "done" ? "مكتمل" : item.status === "canceled" ? "ملغي" : "قيد المتابعة"}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500 sm:text-sm">
                      <span className="inline-flex items-center gap-1"><CalendarDaysIcon className="h-4 w-4" />{formatDateTime(item.date, item.time)}</span>
                      <span>{item.projectName || "موعد عام"}</span>
                    </div>
                    {item.notes ? <p className="mt-3 text-sm leading-7 text-slate-600">{item.notes}</p> : null}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outlined" size="sm" className="flex items-center gap-2 rounded-xl border-brand-200 text-brand-700" onClick={() => handleEdit(item)}><PencilSquareIcon className="h-4 w-4" />تعديل</Button>
                    <Button variant="outlined" size="sm" className="flex items-center gap-2 rounded-xl border-red-200 text-red-600" onClick={() => handleDelete(item.id)}><TrashIcon className="h-4 w-4" />حذف</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
